<template>
  <div class="legal-page">
    <div class="legal-container">
      <!-- Hero Section -->
      <div class="legal-hero">
        <h1>Privacy & Shipping Policies</h1>
        <p class="lead">How we protect your data and deliver your orders</p>
      </div>

      <!-- Main Content -->
      <div class="legal-content">
        <!-- Privacy Policy -->
        <section class="legal-section">
          <h2>Privacy Policy</h2>
          <p><strong>Effective Date:</strong> December 2024</p>
          <p>At REGIIS COSMETICS, we are committed to protecting your privacy and ensuring the security of your personal information. This Privacy Policy explains how we collect, use, disclose, and safeguard your data.</p>
        </section>

        <!-- Information We Collect -->
        <section class="legal-section">
          <h3>Information We Collect</h3>
          <p>We collect information that you provide directly to us:</p>
          <ul>
            <li><strong>Account Information:</strong> Name, email address, phone number, and password</li>
            <li><strong>Payment Information:</strong> Billing address and payment details (processed securely by our payment partners)</li>
            <li><strong>Shipping Information:</strong> Delivery address and contact details</li>
            <li><strong>Order History:</strong> Products purchased, order dates, and transaction details</li>
            <li><strong>Communications:</strong> Messages, reviews, and customer support interactions</li>
          </ul>

          <p>We also automatically collect certain information:</p>
          <ul>
            <li><strong>Device Information:</strong> IP address, browser type, operating system</li>
            <li><strong>Usage Data:</strong> Pages visited, products viewed, time spent on site</li>
            <li><strong>Cookies:</strong> Small data files stored on your device (see Cookie Policy below)</li>
          </ul>
        </section>

        <!-- How We Use Your Information -->
        <section class="legal-section">
          <h3>How We Use Your Information</h3>
          <p>We use your information to:</p>
          <ul>
            <li>Process and fulfill your orders</li>
            <li>Communicate with you about your orders and account</li>
            <li>Provide customer support and respond to inquiries</li>
            <li>Improve our website, products, and services</li>
            <li>Send promotional emails (with your consent)</li>
            <li>Detect and prevent fraud and security threats</li>
            <li>Comply with legal obligations</li>
          </ul>
        </section>

        <!-- Information Sharing -->
        <section class="legal-section">
          <h3>Information Sharing & Disclosure</h3>
          <p>We do not sell your personal information. We may share your data with:</p>
          <ul>
            <li><strong>Service Providers:</strong> Payment processors, shipping companies, and technology partners</li>
            <li><strong>Legal Requirements:</strong> When required by law or to protect our rights</li>
            <li><strong>Business Transfers:</strong> In connection with a merger, acquisition, or sale of assets</li>
          </ul>
        </section>

        <!-- Data Security -->
        <section class="legal-section">
          <h3>Data Security</h3>
          <p>We implement industry-standard security measures to protect your information:</p>
          <ul>
            <li>SSL encryption for all data transmission</li>
            <li>Secure payment processing through trusted partners</li>
            <li>Regular security audits and updates</li>
            <li>Restricted access to personal information</li>
            <li>Data backup and disaster recovery procedures</li>
          </ul>
        </section>

        <!-- Your Rights -->
        <section class="legal-section">
          <h3>Your Rights</h3>
          <p>You have the right to:</p>
          <ul>
            <li>Access your personal data</li>
            <li>Correct inaccurate information</li>
            <li>Request deletion of your data</li>
            <li>Opt-out of marketing communications</li>
            <li>Object to certain data processing</li>
          </ul>
          <p>To exercise these rights, contact us at <a href="mailto:privacy@REGIIS COSMETICS.com">privacy@REGIIS COSMETICS.com</a></p>
        </section>

        <!-- Cookie Policy -->
        <section class="legal-section">
          <h3>Cookie Policy</h3>
          <p>We use cookies and similar technologies to:</p>
          <ul>
            <li>Keep you logged in to your account</li>
            <li>Remember your preferences and cart items</li>
            <li>Analyze site traffic and user behavior</li>
            <li>Personalize your shopping experience</li>
          </ul>
          <p>You can control cookies through your browser settings. Note that disabling cookies may limit site functionality.</p>
        </section>

        <!-- Shipping Policy -->
        <section class="legal-section">
          <h2>Shipping Policy</h2>
          <p><strong>Last Updated:</strong> December 2024</p>
        </section>

        <!-- Shipping Methods -->
        <section class="legal-section">
          <h3>Shipping Methods & Rates</h3>
          <p>We offer multiple shipping options to meet your needs:</p>

          <div class="info-box">
            <h4>Standard Shipping (3-7 Business Days)</h4>
            <p>Cost varies by location and order weight. Calculated at checkout.</p>
          </div>

          <div class="info-box">
            <h4>Express Shipping (1-3 Business Days)</h4>
            <p>Available for most locations. Higher shipping fee applies.</p>
          </div>

          <div class="info-box">
            <h4>Free Shipping</h4>
            <p>Available on orders over ₦50,000 within Lagos. Terms apply.</p>
          </div>
        </section>

        <!-- Delivery Areas -->
        <section class="legal-section">
          <h3>Delivery Areas</h3>
          <p>We currently ship to all states in Nigeria. Delivery times vary by location:</p>
          <ul>
            <li><strong>Lagos:</strong> 1-3 business days</li>
            <li><strong>Major Cities:</strong> 2-5 business days</li>
            <li><strong>Other Locations:</strong> 4-7 business days</li>
          </ul>
        </section>

        <!-- Order Processing -->
        <section class="legal-section">
          <h3>Order Processing</h3>
          <ul>
            <li>Orders are processed Monday - Saturday (excluding holidays)</li>
            <li>Orders placed before 2:00 PM are processed same day</li>
            <li>You'll receive tracking information via email once shipped</li>
            <li>Processing time is separate from delivery time</li>
          </ul>
        </section>

        <!-- Delivery Process -->
        <section class="legal-section">
          <h3>Delivery Process</h3>
          <p>What to expect when your order arrives:</p>
          <ul>
            <li>Delivery partner will call you before arrival</li>
            <li>Valid ID may be required for delivery confirmation</li>
            <li>Someone must be present to receive the package</li>
            <li>Inspect package before signing for delivery</li>
            <li>Report any damage immediately to delivery agent</li>
          </ul>
        </section>

        <!-- Failed Delivery -->
        <section class="legal-section">
          <h3>Failed Delivery Attempts</h3>
          <p>If delivery fails due to:</p>
          <ul>
            <li><strong>Incorrect Address:</strong> Contact us immediately to correct</li>
            <li><strong>Recipient Unavailable:</strong> Delivery partner will attempt 2 more times</li>
            <li><strong>Refused Delivery:</strong> Return shipping fees apply</li>
          </ul>
        </section>

        <!-- Tracking -->
        <section class="legal-section">
          <h3>Order Tracking</h3>
          <p>Track your order easily:</p>
          <ul>
            <li>Log in to your account and view order history</li>
            <li>Click on order number for detailed tracking</li>
            <li>Use tracking number provided in email</li>
            <li>Contact customer service for assistance</li>
          </ul>
        </section>

      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'Policies',
  mounted() {
    window.scrollTo(0, 0)
  }
}
</script>
